import { create } from 'zustand';
import { useEffect } from 'react';
import { useSidebarState } from '@/stores/useSidebarState';
import { useHelpDrawerStore } from '@/stores/useHelpDrawer';

interface ShortcutsPanelStore {
  open: boolean;
  setOpen: (v: boolean) => void;
  toggle: () => void;
}

/** Global store for the keyboard-shortcuts help dialog. */
export const useShortcutsPanelStore = create<ShortcutsPanelStore>((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
  toggle: () => set((s) => ({ open: !s.open })),
}));

function isEditableTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

/**
 * Registers global keyboard shortcuts that are NOT route-specific:
 *  - "?"  → open the keyboard-shortcuts help dialog
 *  - "["  → collapse the sidebar
 *  - "]"  → expand the sidebar
 *
 * All shortcuts are ignored while the user is typing in an input,
 * textarea, select or contenteditable element. Mount once at app root.
 */
export function useKeyboardShortcuts() {
  const toggleHelp = useShortcutsPanelStore((s) => s.toggle);
  const setSidebar = useSidebarState((s) => s.setExpanded);
  const toggleHelpDrawer = useHelpDrawerStore((s) => s.toggle);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Ctrl/Cmd + ? → toggle contextual help drawer (works even in inputs)
      if ((e.metaKey || e.ctrlKey) && (e.key === '?' || (e.shiftKey && e.key === '/'))) {
        e.preventDefault();
        toggleHelpDrawer();
        return;
      }

      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isEditableTarget(e.target)) return;

      if (e.key === '?' || (e.shiftKey && e.key === '/')) {
        e.preventDefault();
        toggleHelp();
        return;
      }
      if (e.key === '[') {
        e.preventDefault();
        setSidebar(false);
        return;
      }
      if (e.key === ']') {
        e.preventDefault();
        setSidebar(true);
        return;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleHelp, setSidebar, toggleHelpDrawer]);
}
